import { useState } from 'react'
import { FormControl } from 'react-bootstrap'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import {
  NumberFormatValues,
  NumericFormat,
  SourceInfo,
} from 'react-number-format'
import { ErrorLabel } from '../ErrorLabel'
import * as S from './styles'

export interface NumericInputProps<T extends FieldValues> {
  control: Control<T, any>
  fieldName: Path<T>
  label?: string
  decimalScale?: number
  allowNegative?: boolean
  disabled?: boolean
  optional?: boolean
  noMargin?: boolean
  getInputClassName?: (isFocused: boolean, hasValue: boolean) => string
  /** Called after the form value updates, with the new numeric value (or undefined when cleared). */
  onValueChange?: (value: number | undefined) => void
}

export function NumericInput<T extends FieldValues>({
  fieldName,
  label,
  control,
  decimalScale = 0,
  allowNegative = true,
  disabled,
  optional,
  noMargin,
  getInputClassName,
  onValueChange,
}: NumericInputProps<T>) {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name: fieldName, control })

  const [isFocused, setIsFocused] = useState(false)

  const _onChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    const isUserEvent = sourceInfo.source === 'event'
    if (values.value === '') {
      onChange(undefined)
      if (isUserEvent) onValueChange?.(undefined)
    } else {
      const numericValue = values.floatValue
      onChange(numericValue)
      if (isUserEvent) onValueChange?.(numericValue)
    }
  }

  const hasValue = value !== undefined && value !== null

  return (
    <S.Wrapper noMargin={noMargin}>
      <label
        className="text-base font-medium text-gray-800 select-none mb-1"
        htmlFor={fieldName}
      >
        {label}
        {optional && (
          <span className="italic text-sm font-normal"> - optional</span>
        )}
      </label>
      <NumericFormat
        disabled={disabled}
        allowNegative={allowNegative}
        onValueChange={_onChange}
        thousandSeparator={true}
        allowedDecimalSeparators={decimalScale ? ['.'] : []}
        decimalScale={decimalScale}
        value={value ?? ''}
        customInput={CustomInput}
        className={`${error?.message ? 'input-error' : ''}`}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        isFocused={isFocused}
        hasValue={hasValue}
        getInputClassName={getInputClassName}
      />
      {error?.message && <ErrorLabel message={error?.message} />}
    </S.Wrapper>
  )
}

const CustomInput = (props: any) => {
  const { isFocused, hasValue, getInputClassName, ...restProps } = props

  const customClassName = getInputClassName
    ? getInputClassName(isFocused, hasValue)
    : ''

  return (
    <FormControl
      {...restProps}
      className={`${restProps.className} ${customClassName}`}
    />
  )
}
